import { create } from 'zustand';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import type { PreparedPicture } from '@/loginPicture';
import { onWindowMessage, tellOtherWindows } from './channel';

export type NumberGrouping = 'international' | 'lakh';
export type DateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'DD-MMM-YYYY';
export type TimeFormat = '12h' | '24h';

/** The MD's sign-in picture: everything about it but the bytes, which are at `url`. */
export type LoginPicture = components['schemas']['LoginPictureDto'];

/** The company's own details and formats. One row, shared by everyone. */
export interface CompanySettings {
  nameEn: string;
  nameUr: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  taxNumber: string | null;
  logoDataUrl: string | null;
  currencyCode: string;
  currencySymbol: string;
  currencyDecimals: number;
  numberGrouping: NumberGrouping;
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  timezone: string;
  fiscalYearStartMonth: number;
  weekStartsOn: number;
  idleLockMinutes: number;
  printFooter: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
  /**
   * null: the built-in picture. Left out (undefined) until the server has
   * said, so the sign-in screen can tell "none" from "not known yet".
   */
  loginPicture?: LoginPicture | null;
}

export type CompanyPatch = Partial<
  Omit<CompanySettings, 'updatedAt' | 'updatedByName' | 'loginPicture'>
>;

/**
 * What the app used before company settings existed, and what it shows until
 * the server answers — so a first paint never shows blank currency or dates.
 */
export const DEFAULT_COMPANY: CompanySettings = {
  nameEn: 'Poultry MS',
  nameUr: null,
  address: null,
  phone: null,
  email: null,
  taxNumber: null,
  logoDataUrl: null,
  currencyCode: 'PKR',
  currencySymbol: 'Rs',
  currencyDecimals: 0,
  numberGrouping: 'international',
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '12h',
  timezone: 'Asia/Karachi',
  fiscalYearStartMonth: 7,
  weekStartsOn: 1,
  idleLockMinutes: 15,
  printFooter: null,
};

/**
 * The company's own formats, kept on the machine.
 *
 * Without this, a reload with no network falls back to the defaults above and
 * every figure quietly changes shape — 49,35,704 becomes 4,935,704, the same
 * money in a grouping the company does not use. A number that looks different
 * is a number people stop trusting, so the last known formats are remembered
 * and used until the server can be asked again.
 *
 * Not per person: these are the company's, the same for everyone at that PC,
 * and there is nothing private in them.
 */
const REMEMBERED_KEY = 'poultry.company';

function remember(company: CompanySettings): void {
  try {
    localStorage.setItem(REMEMBERED_KEY, JSON.stringify(company));
  } catch {
    // Private mode: the formats fall back to the defaults, as before.
  }
}

function remembered(): CompanySettings | null {
  try {
    const raw = localStorage.getItem(REMEMBERED_KEY);
    return raw ? { ...DEFAULT_COMPANY, ...(JSON.parse(raw) as CompanySettings) } : null;
  } catch {
    return null;
  }
}

interface CompanyState {
  company: CompanySettings;
  loaded: boolean;
  load: () => Promise<void>;
  /** Saves and returns the saved settings; throws with the server's message. */
  save: (patch: CompanyPatch) => Promise<CompanySettings>;
  /** The sign-in screen's name, logo and picture, which anyone may see. */
  loadPublic: () => Promise<void>;
  /** True once loadPublic has had an answer, or given up waiting for one. */
  publicLoaded: boolean;
  /** Throws with the server's message. */
  saveLoginPicture: (picture: PreparedPicture) => Promise<LoginPicture>;
  removeLoginPicture: () => Promise<void>;
}

export const useCompany = create<CompanyState>((set) => ({
  // What was last seen on this machine, so the first paint after an offline
  // reload already has the right currency and dates.
  company: remembered() ?? DEFAULT_COMPANY,
  loaded: false,
  publicLoaded: false,

  load: async () => {
    try {
      const company = await api.get('/api/v1/company');
      const merged = { ...DEFAULT_COMPANY, ...company };
      remember(merged);
      set({ company: merged, loaded: true });
    } catch {
      // No answer: keep whatever is on screen, which is either what this
      // machine last saw or the defaults. Never reset mid-session.
    }
  },

  save: async (patch) => {
    const saved = (await api.patch<CompanySettings>('/api/v1/company', patch)) as CompanySettings;
    const merged = { ...DEFAULT_COMPANY, ...saved };
    remember(merged);
    set({ company: merged, loaded: true });
    tellOtherWindows({ type: 'company' });
    return saved;
  },

  loadPublic: async () => {
    try {
      const res = await fetch('/api/v1/company/public');
      if (!res.ok) return;
      const pub = (await res.json()) as Pick<
        CompanySettings,
        'nameEn' | 'nameUr' | 'logoDataUrl' | 'loginPicture'
      >;
      set((s) => {
        const company = { ...s.company, ...pub, loginPicture: pub.loginPicture ?? null };
        // Kept so the next cold start on this machine knows the picture at
        // once, rather than flashing the built-in one first. Only its
        // details: the bytes stay in the browser's own image cache.
        remember(company);
        return { company };
      });
    } catch {
      // The sign-in screen shows the product name and built-in picture instead.
    } finally {
      set({ publicLoaded: true });
    }
  },

  saveLoginPicture: async (picture) => {
    const query = new URLSearchParams({
      tint: picture.tint,
      lumaTop: String(picture.lumaTop),
      lumaMid: String(picture.lumaMid),
      lumaBottom: String(picture.lumaBottom),
      palette: picture.palette,
    });
    const saved = await api.upload<LoginPicture>(
      `/api/v1/company/login-picture?${query.toString()}`,
      picture.blob,
    );
    set((s) => {
      const company = { ...s.company, loginPicture: saved };
      remember(company);
      return { company };
    });
    tellOtherWindows({ type: 'company' });
    return saved;
  },

  removeLoginPicture: async () => {
    await api.delete('/api/v1/company/login-picture');
    set((s) => {
      const company = { ...s.company, loginPicture: null };
      remember(company);
      return { company };
    });
    tellOtherWindows({ type: 'company' });
  },
}));

onWindowMessage((m) => {
  if (m.type !== 'company') return;
  void useCompany.getState().load();
  // A window still on the sign-in screen has no session to load() with; the
  // public details are what it shows, so a new picture appears there too.
  void useCompany.getState().loadPublic();
});

/** Current settings, for plain functions that format values. */
export function company(): CompanySettings {
  return useCompany.getState().company;
}
